import { ld } from "@/lib/theme/light-dark";
import {
  Building2,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  Contact,
  Send,
} from "lucide-react";
/**
 * Floating-phone overlay for the Formulare module — a filled-in handover form
 * (Übergabeformular) shown on the right edge of the app window, bleeding below
 * its bottom edge. A bespoke compact device (48×21 island, custom iOS status
 * bar, custom home indicator) matching the prototype 1:1 (`isFormulare`
 * overlay, Hausverwaltung Hero.dc.html lines 2006–2030). Built inline rather
 * than via {@link IPhoneFrame} because this overlay's island / status bar /
 * home indicator differ from the shared frame (same convention as
 * module-portal-phone.tsx).
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";

/** A single field label ("Liegenschaft *") + filled accent pill row. */
function FilledField(props: {
  readonly label: string;
  readonly icon: ReactNode;
  readonly value: string;
  readonly labelMargin: string;
}): ReactNode {
  return (
    <>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: H.inkFaint,
          margin: props.labelMargin,
        }}
      >
        {props.label} <span style={{ color: ld("#D14B3D", "#fd8170") }}>*</span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: H.accentSoft,
          border: `1px solid ${H.accentBorder}`,
          borderRadius: 11,
          padding: "12px 13px",
        }}
      >
        <span style={{ color: H.accent, display: "inline-flex" }}>
          {props.icon}
        </span>
        <span style={{ fontSize: 14.5, fontWeight: 600, color: H.ink }}>
          {props.value}
        </span>
      </div>
    </>
  );
}

/** iOS status bar (9:41 + signal / wifi / battery glyphs), bespoke to the overlay. */
function StatusBar(): ReactNode {
  const t = useTranslations("marketing.moduleFormularePhone");
  return (
    <div
      style={{
        flexShrink: 0,
        height: 46,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 13px",
        background: ld("#fff", "#181b1f"),
      }}
    >
      <span
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "#000",
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {t("k941")}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <svg
          width="17"
          height="12"
          viewBox="0 0 17 12"
          fill="#000"
          style={{ display: "block" }}
          aria-hidden="true"
        >
          <rect x="0" y="7.5" width="3" height="4.5" rx="1" />
          <rect x="4.67" y="5" width="3" height="7" rx="1" />
          <rect x="9.33" y="2.5" width="3" height="9.5" rx="1" />
          <rect x="14" y="0" width="3" height="12" rx="1" />
        </svg>
        <svg
          width="16"
          height="12"
          viewBox="0 0 16 12"
          fill="none"
          stroke="#000"
          strokeWidth="1.55"
          strokeLinecap="round"
          style={{ display: "block" }}
          aria-hidden="true"
        >
          <path d="M1.72 5.23 A8.2 8.2 0 0 1 14.28 5.23" />
          <path d="M4.63 7.67 A4.4 4.4 0 0 1 11.37 7.67" />
          <circle cx="8" cy="10" r="1" fill="#000" stroke="none" />
        </svg>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}
        >
          <span
            style={{
              position: "relative",
              width: 23,
              height: 11.5,
              border: "1px solid rgba(0,0,0,0.35)",
              borderRadius: 3,
              boxSizing: "border-box",
              padding: 1.4,
            }}
          >
            <span
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                background: "#000",
                borderRadius: 1.5,
              }}
            />
          </span>
          <span
            style={{
              width: 1.5,
              height: 4,
              background: "rgba(0,0,0,0.35)",
              borderRadius: "0 1.5px 1.5px 0",
            }}
          />
        </span>
      </span>
    </div>
  );
}

/** The right-edge floating phone overlay for the Formulare module. */
export function ModuleFormularePhone(): ReactNode {
  const t = useTranslations("marketing.moduleFormularePhone");
  return (
    <div
      style={{
        position: "absolute",
        right: 18,
        bottom: -42,
        zIndex: 30,
        width: 260,
      }}
    >
      <div
        style={{
          position: "relative",
          background: "linear-gradient(150deg,#43464D 0%,#0C0E12 56%)",
          borderRadius: 44,
          padding: 5,
          boxShadow:
            `0 78px 104px -34px ${ld("rgba(11,15,25,0.6)", "rgba(0,0,0,0.85)")}, 0 34px 60px -24px ${ld("rgba(11,15,25,0.4)", "rgba(0,0,0,0.65)")}, inset 0 2px 1.5px ${ld("rgba(255,255,255,0.18)", "rgba(255,255,255,0.06)")}, inset 0 0 0 1.5px rgba(255,255,255,0.06)`,
        }}
      >
        <div
          style={{
            position: "relative",
            background: ld("#fff", "#181b1f"),
            borderRadius: 38,
            overflow: "hidden",
            height: 522,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Dynamic Island */}
          <div
            style={{
              position: "absolute",
              top: 11,
              left: "calc(50% - 24px)",
              width: 48,
              height: 21,
              background: "#000",
              borderRadius: 12,
              zIndex: 8,
            }}
          />
          <StatusBar />

          {/* Form picker header */}
          <div
            style={{
              flexShrink: 0,
              padding: "4px 12px 11px",
              background: ld("#fff", "#181b1f"),
              borderBottom: `1px solid ${ld("#F0F0F0", "#2c2f33")}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                color: H.ink,
                display: "inline-flex",
                flexShrink: 0,
                marginLeft: -2,
              }}
            >
              <ChevronLeft width={23} height={23} strokeWidth={2.2} />
            </span>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: ld("#F4F4F2", "#292c30"),
                border: `1px solid ${ld("#E8E8E4", "#34373b")}`,
                borderRadius: 9,
                padding: "7px 11px",
              }}
            >
              <span
                style={{
                  color: H.inkFaint,
                  display: "inline-flex",
                  flexShrink: 0,
                }}
              >
                <ClipboardList width={15} height={15} strokeWidth={1.9} />
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: H.ink,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("uebergabeformular")}
              </span>
              <span
                style={{ color: ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)"), display: "inline-flex" }}
              >
                <ChevronDown width={15} height={15} strokeWidth={2} />
              </span>
            </div>
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              padding: "18px 18px 4px",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: H.ink,
                letterSpacing: "-0.015em",
                marginBottom: 17,
              }}
            >
              {t("uebergabe")}
            </div>
            <FilledField
              label={t("liegenschaft")}
              icon={<Building2 width={17} height={17} strokeWidth={1.9} />}
              value="Ahornweg 5"
              labelMargin="0 0 7px"
            />
            <FilledField
              label={t("kontakt")}
              icon={<Contact width={17} height={17} strokeWidth={1.9} />}
              value="Petra Sommer"
              labelMargin="16px 0 7px"
            />
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: H.inkFaint,
                margin: "16px 0 7px",
              }}
            >
              {t.rich("unterschriftPflicht", {
                req: (chunks: ReactNode): ReactNode => (
                  <span style={{ color: ld("#D14B3D", "#fd8170") }}>{chunks}</span>
                ),
              })}
            </div>
            <div
              style={{
                position: "relative",
                border: `1px solid ${ld("#E9E9E4", "#33363a")}`,
                borderRadius: 11,
                height: 68,
                background: ld("#FAFAF9", "#1e2125"),
                overflow: "hidden",
              }}
            >
              <svg
                viewBox="0 0 224 60"
                width="100%"
                height="60"
                preserveAspectRatio="none"
                style={{ position: "absolute", left: 0, top: 6 }}
                aria-hidden="true"
              >
                <path
                  d="M12 40 C22 17, 33 17, 38 33 C41 44, 50 44, 58 28 C63 17, 71 19, 71 33 C71 43, 80 43, 89 31 C105 12, 119 47, 139 29 C151 18, 164 41, 185 25 C197 16, 207 33, 219 27"
                  fill="none"
                  stroke={ld("#2A3B52", "#d5d9df")}
                  strokeWidth="2.3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          {/* Submit */}
          <div
            style={{
              flexShrink: 0,
              padding: "10px 18px 12px",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                height: 48,
                borderRadius: 14,
                background: H.accent,
                color: ld("#fff", "#181b1f"),
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              <Send width={16} height={16} strokeWidth={1.9} />
              {t("absenden")}
            </span>
          </div>

          {/* Home indicator */}
          <div
            style={{
              flexShrink: 0,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <span
              style={{
                width: 128,
                height: 5,
                borderRadius: 3,
                background: H.ink,
                opacity: 0.9,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
